import { Flex, Form, Typography } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import { AnimatePresence, motion } from "motion/react"
import React from "react"

import { budgetFormat } from "../../../../../../utils/number/budget-format.ts"
import type { TemplateDocumentFormData } from "../../../../types/template-document-form-data.tsx"

const { Text } = Typography

interface ScopeOfWorkBlockFooterProps {
  form: FormInstance<TemplateDocumentFormData>
}

export function ScopeOfWorkBlockFooter({ form }: ScopeOfWorkBlockFooterProps) {
  const phases = Form.useWatch("phases", form) || []

  const totalBudget = phases.reduce(
    (sum: number, item) => sum + +(item?.budget || 0),
    0
  )

  const calculateTotalTimeline = () => {
    if (!phases.length) return "—"

    const totalDays = phases.reduce((sum: number, phase) => {
      const duration = phase?.duration_days || 0
      const type = phase?.duration_type || "days"

      switch (type) {
        case "weeks":
          return sum + duration * 7
        case "months":
          return sum + duration * 30
        case "years":
          return sum + duration * 365
        default:
          return sum + duration
      }
    }, 0)

    if (totalDays === 0) return "—"

    const years = Math.floor(totalDays / 365)
    const remainingAfterYears = totalDays % 365
    const months = Math.floor(remainingAfterYears / 30)
    const days = remainingAfterYears % 30

    const parts: string[] = []
    if (years > 0) parts.push(`${years} ${years === 1 ? "year" : "years"}`)
    if (months > 0) parts.push(`${months} ${months === 1 ? "month" : "months"}`)
    if (days > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`)

    return parts.join(", ")
  }

  return (
    <Flex
      align={"center"}
      className={
        "p-3 mt-2 border bg-[var(--ant-table-footer-bg)] border-border rounded-card"
      }
    >
      <div className="flex-1">
        <Text strong>Total</Text>
      </div>
      <div style={{ width: 270 }}>
        <Text strong>
          <AnimatedText value={calculateTotalTimeline()} />
        </Text>
      </div>
      <div style={{ width: 225 }}>
        <Text strong ellipsis className="truncate max-w-40">
          {budgetFormat(totalBudget)}
        </Text>
      </div>
      <div style={{ width: 50 }} />
    </Flex>
  )
}

function AnimatedText({ value }: { value: string }) {
  return (
    <span style={{ display: "inline-flex", overflow: "hidden" }}>
      <AnimatePresence mode="wait">
        <motion.span
          key={value}
          style={{ display: "inline-flex" }}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {value.split("").map((char, i) => (
            <motion.span
              key={i}
              style={{ display: "inline-block" }}
              variants={{
                hidden: { opacity: 0, y: -16 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.2, delay: i * 0.03 }
                },
                exit: {
                  opacity: 0,
                  y: 16,
                  transition: { duration: 0.15, delay: i * 0.02 }
                }
              }}
            >
              {char === " " ? "\u00A0" : char}
            </motion.span>
          ))}
        </motion.span>
      </AnimatePresence>
    </span>
  )
}
